import { editorNodeIdClass } from './dom.js';
import { queryEditorPreviewNode } from './editorPreviewViewport.js';
import { html } from 'lit';
import {
  clampEditorGridColumns,
  editorGridDefaultColumns,
  editorGridHeightStep,
  resolveEditorImageScale,
} from '../../geometry.js';
import type { WebEditorElementFrame, WebEditorLayoutProject } from '../../layout.js';
import type { WebEditorElementDefinition } from '../../element-definition.js';
import type { EditorFrame, EditorPreviewProfile, EditorPreviewProfileId } from './editorPageConfig.js';
import type { EditorGeometryOperations } from './editorGeometryOperations.js';
import type { EditorGeometryState } from './editorGeometryState.js';

interface EditorGeometryControlsOptions {
  definition?: WebEditorElementDefinition;
  elementId: string;
  frame: WebEditorElementFrame;
  project: WebEditorLayoutProject;
  activeFrame: EditorFrame;
  activePreviewProfile: EditorPreviewProfileId;
  previewProfiles: readonly EditorPreviewProfile[];
  selectPreviewProfile: (profile: EditorPreviewProfile) => void;
  state: EditorGeometryState;
  operations: EditorGeometryOperations;
}

const viewportProperties = (frame: EditorFrame) =>
  frame === 'desktop'
    ? ({ grid: 'gridDesktop', columns: 'gridColumnsDesktop', focus: 'imageFocusDesktop', mask: 'imageMaskDesktop' } as const)
    : ({ grid: 'gridMobile', columns: 'gridColumnsMobile', focus: 'imageFocusMobile', mask: 'imageMaskMobile' } as const);

const changedNumber = (event: Event, fallback: number) => {
  const value = Number((event.target as HTMLInputElement).value);
  return Number.isFinite(value) ? value : fallback;
};

export const renderEditorGeometryControls = (options: EditorGeometryControlsOptions) => {
  const definition = options.definition;
  if (!definition) return '';
  const properties = viewportProperties(options.activeFrame);
  const placement = options.frame[properties.grid];
  const maskHostFrame = definition.maskHostId ? options.project.elementFrames[definition.maskHostId] : undefined;
  const maskHostItem = options.activeFrame === 'desktop' ? maskHostFrame?.autoLayoutItemDesktop : maskHostFrame?.autoLayoutItemMobile;
  const fluidSize = maskHostFrame?.fluidSize ?? options.frame.fluidSize;
  const fluidNode = fluidSize ? queryEditorPreviewNode(`.vp-editor-preview-page .${editorNodeIdClass(definition.maskHostId ?? options.elementId)}`) : null;
  const maskWidth = fluidNode?.offsetWidth ?? fluidSize?.width ?? maskHostItem?.fixedWidth ?? placement?.maskWidth;
  const maskHeight = fluidNode?.offsetHeight ?? fluidSize?.height ?? maskHostItem?.fixedHeight ?? placement?.maskHeight;
  const parentFrame = definition.parentId ? options.project.elementFrames[definition.parentId] : undefined;
  const parentAutoLayout = options.activeFrame === 'desktop' ? parentFrame?.autoLayoutDesktop : parentFrame?.autoLayoutMobile;
  const columns = clampEditorGridColumns(
    options.frame[properties.columns] ?? parentFrame?.[properties.columns] ?? editorGridDefaultColumns,
  );
  const focus = options.frame[properties.focus] ?? options.frame.imageFocusDesktop ?? { x: 50, y: 50 };
  const unlocked = options.state.isUnlocked(options.elementId);
  const hasLegacyOffset = Boolean(options.frame.x || options.frame.y);
  const isImage = definition.kind === 'image';
  const focusMode = options.state.focusEditingElementId === options.elementId;
  const focusDisabled = options.frame.objectFit === 'fill';
  const imageScale = resolveEditorImageScale(options.frame, options.activePreviewProfile);
  const activeProfile = options.previewProfiles.find(profile => profile.id === options.activePreviewProfile);
  const hasLocalScale = options.frame.imageScaleByBreakpoint?.[options.activePreviewProfile] !== undefined;
  const structuralMask = Boolean(definition.maskHostId);
  const maskEnabled = structuralMask || (options.frame[properties.mask] ?? options.frame.imageMaskDesktop ?? false);
  const imageMode = options.state.imageEditingMode;
  const toggleFocus = () => options.state.setFocusEditingElementId(focusMode ? null : options.elementId);
  const setColumns = (value: number) => options.operations.setGridColumns(options.elementId, value);
  const updatePlacement = (update: Parameters<EditorGeometryOperations['updatePlacement']>[1]) =>
    options.operations.updatePlacement(options.elementId, update);

  return html`
    <section class="vp-editor-panel-section vp-editor-geometry-card">
      <div class="vp-editor-geometry-heading">
        <div>
          <p class="vp-editor-kicker">Geometrie</p>
          <small>${options.activeFrame === 'desktop' ? 'Desktop' : 'Mobile'} · Sitzung</small>
        </div>
        <button
          type="button"
          class="vp-editor-lock-control ${unlocked ? 'is-unlocked' : ''}"
          aria-pressed=${String(unlocked)}
          @click=${() => options.state.toggleLock(options.elementId)}
        >
          <span class=${unlocked ? 'icon-[material-symbols--lock-open-right-outline]' : 'icon-[material-symbols--lock-outline]'}></span>
          ${unlocked ? 'Entsperrt' : 'Gesperrt'}
        </button>
      </div>

      ${hasLegacyOffset
        ? html`<div class="vp-editor-legacy-offset" role="status">
            <span class="icon-[material-symbols--warning-outline]" aria-hidden="true"></span>
            <p>Dieses Element hat noch einen freien X/Y-Versatz.</p>
            <button type="button" @click=${() => options.operations.adoptLegacyOffset(options.elementId)}>
              ${parentAutoLayout ? 'In Auto Layout übernehmen' : 'Auf Raster übernehmen'}
            </button>
            <button type="button" @click=${() => options.operations.resetLegacyOffset(options.elementId)}>
              Versatz zurücksetzen
            </button>
          </div>`
        : ''}

      ${definition.parentId && !definition.maskHostId && !parentAutoLayout
        ? html`
            <div class="vp-editor-geometry-row vp-editor-columns-row">
              <label for="vp-editor-grid-columns">Spalten</label>
              <div class="vp-editor-number-stepper">
                <button type="button" aria-label="Eine Spalte weniger" ?disabled=${!unlocked || columns <= 1} @click=${() => setColumns(columns - 1)}><span class="icon-[material-symbols--remove]"></span></button>
                <input
                  id="vp-editor-grid-columns"
                  type="number"
                  min="1"
                  max="20"
                  .value=${String(columns)}
                  ?disabled=${!unlocked}
                  @change=${(event: Event) => setColumns(changedNumber(event, columns))}
                />
                <button type="button" aria-label="Eine Spalte mehr" ?disabled=${!unlocked || columns >= 20} @click=${() => setColumns(columns + 1)}><span class="icon-[material-symbols--add]"></span></button>
              </div>
              <small>1–20${options.state.overlayRect?.elementId === options.elementId ? ` · ${options.state.overlayRect.width} px` : ''}</small>
            </div>
            <div class="vp-editor-grid-preview" style=${`--vp-editor-control-columns: ${columns}`} aria-hidden="true">
              ${Array.from({ length: columns }, (_, index) => html`<i class=${placement && index + 1 >= placement.columnStart && index + 1 < placement.columnStart + placement.columnSpan ? 'is-active' : ''}></i>`)}
            </div>
            <div class="vp-editor-geometry-fields">
              <label>
                Start
                <input
                  type="number"
                  min="1"
                  max=${String(columns)}
                  .value=${String(placement?.columnStart ?? 1)}
                  ?disabled=${!unlocked || hasLegacyOffset}
                  @change=${(event: Event) => updatePlacement({ columnStart: changedNumber(event, placement?.columnStart ?? 1) })}
                />
              </label>
              <label>
                Rasterbreite
                <input
                  type="number"
                  min="1"
                  max=${String(columns)}
                  .value=${String(placement?.columnSpan ?? columns)}
                  ?disabled=${!unlocked || hasLegacyOffset}
                  @change=${(event: Event) => updatePlacement({ columnSpan: changedNumber(event, placement?.columnSpan ?? columns) })}
                />
              </label>
              <label>
                Reihenfolge
                <input
                  type="number"
                  min="0"
                  .value=${String(placement?.order ?? 0)}
                  ?disabled=${!unlocked || hasLegacyOffset}
                  @change=${(event: Event) => updatePlacement({ order: changedNumber(event, placement?.order ?? 0) })}
                />
              </label>
            </div>
            ${!unlocked
              ? html`<p class="vp-editor-geometry-hint"><span class="icon-[material-symbols--lock-outline]"></span> Zum Verschieben oder Skalieren zuerst entsperren.</p>`
              : ''}
            ${placement
              ? html`<button type="button" class="vp-editor-geometry-reset" @click=${() => options.operations.resetGrid(options.elementId)}>
                  ${options.activeFrame === 'desktop' ? 'Desktop' : 'Mobile'}-Raster zurücksetzen
                </button>`
              : ''}
          `
        : html`<p class="vp-editor-geometry-hint">${definition.maskHostId ? 'Die Bildmaske wird als Auto-Layout-Kind skaliert.' : parentAutoLayout ? 'Position und Größe folgen dem Auto Layout des Eltern-Frames.' : 'Dieses Root-Element wird über seine Section-Reihenfolge positioniert.'}</p>`}

      ${isImage
        ? html`
            <div class="vp-editor-image-geometry">
              <div class="vp-editor-image-edit-tabs" role="tablist" aria-label="Bild oder Maske bearbeiten">
                <button
                  type="button"
                  role="tab"
                  class=${imageMode === 'image' ? 'is-active' : ''}
                  aria-selected=${String(imageMode === 'image')}
                  @click=${() => options.state.setImageEditingMode('image')}
                ><span class="icon-[material-symbols--image-outline]" aria-hidden="true"></span>Bildausschnitt</button>
                <button
                  type="button"
                  role="tab"
                  class=${imageMode === 'mask' ? 'is-active' : ''}
                  aria-selected=${String(imageMode === 'mask')}
                  @click=${() => {
                    options.state.setFocusEditingElementId(null);
                    options.state.setImageEditingMode('mask');
                  }}
                ><span class="icon-[material-symbols--crop]" aria-hidden="true"></span>Layoutgröße</button>
              </div>
              ${imageMode === 'image'
                ? html`
                    <div class="vp-editor-image-breakpoints" role="tablist" aria-label="Bildgröße je Viewport">
                      ${options.previewProfiles.map(profile => html`<button
                        type="button"
                        role="tab"
                        class=${profile.id === options.activePreviewProfile ? 'is-active' : ''}
                        aria-selected=${String(profile.id === options.activePreviewProfile)}
                        aria-label=${`${profile.label}, ${profile.width} Pixel bearbeiten`}
                        title=${`${profile.label} · ${profile.width} px`}
                        @click=${() => options.selectPreviewProfile(profile)}
                      ><span class=${profile.id === 'mobile' ? 'icon-[material-symbols--smartphone]' : profile.id === 'tablet' ? 'icon-[material-symbols--tablet-mac-outline]' : 'icon-[material-symbols--desktop-windows-outline]'} aria-hidden="true"></span><i class=${options.frame.imageScaleByBreakpoint?.[profile.id] !== undefined ? 'has-value' : ''}></i></button>`)}
                    </div>
                    <div class="vp-editor-image-mode-heading">
                      <div><strong>Zoom im Bildausschnitt</strong><small>${activeProfile?.label ?? 'Viewport'} · ${activeProfile?.width ?? ''} px · ${hasLocalScale ? 'eigener Wert' : 'geerbt'}</small></div>
                      <output>${imageScale}%</output>
                    </div>
                    <div class="vp-editor-image-scale-control">
                      <span class="icon-[material-symbols--photo-size-select-small]" aria-hidden="true"></span>
                      <input
                        type="range"
                        min="25"
                        max="500"
                        step="1"
                        .value=${String(imageScale)}
                        ?disabled=${!unlocked}
                        aria-label="Bildgröße in Prozent"
                        @input=${(event: Event) => {
                          const value = changedNumber(event, imageScale);
                          options.operations.previewImageScale(options.elementId, value);
                          const root = (event.currentTarget as HTMLElement).closest('.vp-editor-image-geometry');
                          const output = root?.querySelector<HTMLOutputElement>('.vp-editor-image-mode-heading output');
                          const number = root?.querySelector<HTMLInputElement>('.vp-editor-image-scale-control input[type="number"]');
                          if (output) output.value = `${Math.round(value)}%`;
                          if (number) number.value = String(Math.round(value));
                        }}
                        @change=${(event: Event) => {
                          const value = changedNumber(event, imageScale);
                          options.operations.clearImageScalePreview(options.elementId);
                          options.operations.setImageScale(options.elementId, value);
                        }}
                      />
                      <div class="vp-editor-unit-input">
                        <input
                          type="number"
                          min="25"
                          max="500"
                          .value=${String(imageScale)}
                          ?disabled=${!unlocked}
                          aria-label="Bildgröße in Prozent"
                          @change=${(event: Event) => options.operations.setImageScale(options.elementId, changedNumber(event, imageScale))}
                        />
                        <span>%</span>
                      </div>
                      <button type="button" class="vp-editor-icon-button" title="Viewport-Wert zurücksetzen" aria-label="Bildgröße für diesen Viewport zurücksetzen" ?disabled=${!unlocked || !hasLocalScale} @click=${() => options.operations.resetImageScale(options.elementId)}><span class="icon-[material-symbols--restart-alt]" aria-hidden="true"></span></button>
                    </div>
                    <p class="vp-editor-geometry-hint"><span class="icon-[material-symbols--drag-pan]" aria-hidden="true"></span>Ziehe an einem runden Eckgriff, um nur das Bild proportional zu skalieren.</p>
                    <div class="vp-editor-focus-heading">
                      <div><strong>Position in der Maske</strong><small>Fokus für ${options.activeFrame === 'desktop' ? 'Desktop' : 'Mobile'}</small></div>
                      <button
                        type="button"
                        class=${focusMode ? 'is-active' : ''}
                        ?disabled=${focusDisabled}
                        aria-pressed=${String(focusMode)}
                        @click=${toggleFocus}
                      >${focusMode ? 'Fertig' : 'Verschieben'}</button>
                    </div>
                    ${focusDisabled
                      ? html`<p class="vp-editor-geometry-hint">Bei „fill“ gibt es keinen Bildausschnitt. Wähle „cover“ oder „contain“.</p>`
                      : html`<div class="vp-editor-focus-fields">
                          <label>X <input type="number" min="0" max="100" .value=${String(focus.x)} @change=${(event: Event) => options.operations.setFocusPoint(options.elementId, changedNumber(event, focus.x), focus.y)} /></label>
                          <label>Y <input type="number" min="0" max="100" .value=${String(focus.y)} @change=${(event: Event) => options.operations.setFocusPoint(options.elementId, focus.x, changedNumber(event, focus.y))} /></label>
                          <button type="button" class="vp-editor-icon-button" aria-label="Bildfokus zentrieren" @click=${() => options.operations.setFocusPoint(options.elementId, 50, 50)}><span class="icon-[material-symbols--center-focus-strong-outline]"></span></button>
                        </div>`}
                  `
                : html`
                    <div class="vp-editor-image-mask-heading">
                      <div>
                        <strong>Responsive Bildgröße</strong>
                        <small>${structuralMask ? 'Diese Layout-Maske gehört zum Bildaufbau.' : 'Breite und Höhe werden proportional auf andere Ansichten übertragen.'}</small>
                      </div>
                      ${structuralMask
                        ? html`<span class="vp-editor-image-mask-status"><span class="icon-[material-symbols--check-circle]" aria-hidden="true"></span>Aktiv</span>`
                        : html`<button
                            type="button"
                            class=${maskEnabled ? 'vp-editor-mask-toggle is-active' : 'vp-editor-mask-toggle'}
                            aria-pressed=${String(maskEnabled)}
                            ?disabled=${!unlocked}
                            @click=${() => options.operations.setImageMaskEnabled(options.elementId, !maskEnabled)}
                          ><span class=${maskEnabled ? 'icon-[material-symbols--visibility]' : 'icon-[material-symbols--add-photo-alternate-outline]'} aria-hidden="true"></span>${maskEnabled ? 'Maske aktiv' : 'Maske hinzufügen'}</button>`}
                    </div>
                    ${isImage
                      ? html`
                          <div class="vp-editor-image-mask-fields">
                            <label>
                              Breite
                              <div class="vp-editor-unit-input">
                                <input
                                  type="number"
                                  min="24"
                                  max="2400"
                                  step="1"
                                  placeholder="Auto"
                                  .value=${maskWidth === undefined ? '' : String(maskWidth)}
                                  ?disabled=${!unlocked || hasLegacyOffset}
                                  @change=${(event: Event) => options.operations.setImageMaskSize(options.elementId, changedNumber(event, maskWidth ?? options.state.overlayRect?.width ?? 320), maskHeight)}
                                />
                                <span>px</span>
                              </div>
                            </label>
                            <label>
                              Höhe
                              <div class="vp-editor-unit-input">
                                <input
                                  type="number"
                                  min="48"
                                  max="2400"
                                  step=${String(editorGridHeightStep)}
                                  placeholder="Auto"
                                  .value=${maskHeight === undefined ? '' : String(maskHeight)}
                                  ?disabled=${!unlocked || hasLegacyOffset}
                                  @change=${(event: Event) => options.operations.setImageMaskSize(options.elementId, maskWidth, changedNumber(event, maskHeight ?? options.state.overlayRect?.height ?? 240))}
                                />
                                <span>px</span>
                              </div>
                            </label>
                          </div>
                          <p class="vp-editor-geometry-hint"><span class="icon-[material-symbols--crop-free]" aria-hidden="true"></span>Gilt für alle Bildschirmgrößen. Nachbarelemente fließen mit. Seitengriffe ändern eine Achse, Ecken erhalten das Verhältnis; mit Shift frei ziehen.</p>
                        `
                      : html`<div class="vp-editor-mask-empty"><span class="icon-[material-symbols--crop]" aria-hidden="true"></span><p><strong>Keine Maske</strong><small>Das vollständige Bild bleibt sichtbar. Füge eine Maske hinzu, um einen eigenen Ausschnitt anzulegen.</small></p></div>`}
                  `}
            </div>
          `
        : ''}
    </section>
  `;
};
